import {
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    TemplateRef,
    ChangeDetectionStrategy,
} from '@angular/core';
import {
    EvaluationInstance,
    EvaluationStandardRecord,
    EvaluationStandardRecordValue,
    EvaluationType,
} from '@masar/features/evaluate/types';
import { EvaluateService } from '@masar/features/evaluate/services/evaluate.service';
import { finalize } from 'rxjs/operators';
import {
    Evaluation,
    EvaluationMatrix,
    EvaluationMatrixType,
    EvaluationStandard,
    KpiResultPeriod,
} from '@masar/common/models';
import { ConfirmService } from '@masar/features/confirm-dialog';
import { TranslateService } from '@ngx-translate/core';
import { NotificationService } from 'mnm-webapp';

@Component({
    selector: 'app-evaluation-instance-detail',
    templateUrl: './evaluation-instance-detail.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class EvaluationInstanceDetailComponent implements OnInit {
    @Input() public entityId?: string;

    @Input() public savedEntityId?: string;

    @Input() public initialEvaluationId?: string;

    @Input() public itemId?: string;

    @Input() public type!: EvaluationType;

    @Input() public mode: 'default' | 'view' = 'default';

    @Input() public customAction?: {
        name: string;
        template: TemplateRef<any>;
        item: KpiResultPeriod;
    };

    @Input() public useGroupedProjectStandards = false;

    @Input() public hideNotes = false;

    @Output() public update = new EventEmitter<EvaluationInstance>();

    public item?: Partial<EvaluationInstance>;

    public isSubmitting = false;

    public hasEmptyValue = false;

    public hasValueOutOfRange = false;

    public hasNoEvaluationDefinition = false;

    public evaluations: Evaluation[] = [];

    public selectedEvaluation: Evaluation | null = null;

    public groupedMatrices: EvaluationMatrix[] = [];

    public standaloneStandards: EvaluationStandard[] = [];

    public isProjectGroupedBehaviorEnabled = false;

    public isNotesHidden = false;

    public isResolvingLayout = false;

    public resolvedMatrixType: EvaluationMatrixType | null = null;

    public readonly guidePreviewLimit = 100;

    private standards: EvaluationStandard[] | undefined;

    private readonly legacyOptionsCache = new Map<number, number[]>();

    private readonly standardIdByKey = new Map<string, string>();

    private flatStandardsForSubmit: EvaluationStandard[] = [];

    private readonly availableByNameAndTarget = new Map<string, string[]>();

    private readonly availableByTarget = new Map<number, string[]>();

    private readonly recordsByStandardId = new Map<
        string,
        EvaluationStandardRecord
    >();

    private recordWithStandardId!: EvaluationStandardRecord & {
        standardId?: string;
    };

    public constructor(
        private readonly evaluateService: EvaluateService,
        private readonly confirmService: ConfirmService,
        private readonly translateService: TranslateService,
        private readonly notificationService: NotificationService
    ) {}

    public ngOnInit(): void {
        this.resolveBehaviorFlags();

        if (this.itemId) {
            this.isResolvingLayout = this.isProjectGroupedBehaviorEnabled;
            this.getItem();
            return;
        }

        this.evaluateService
            .listEvaluations(this.type, this.savedEntityId)
            .subscribe(items => {
                this.evaluations = items;

                if (this.evaluations.length > 0) {
                    this.setSelectedEvaluation(
                        items.find(x => x.id === this.initialEvaluationId) ??
                            items.find(x => x.isDefault) ??
                            items[0]
                    );
                    return;
                }

                this.hasNoEvaluationDefinition = true;
                this.createItem();
            });
    }

    public setSelectedEvaluation(selectedEvaluation: Evaluation): void {
        this.resetEvaluationState(selectedEvaluation);

        if (!this.selectedEvaluation) {
            this.standards = [];
            this.isResolvingLayout = false;
            this.createItem();
            return;
        }

        if (this.isProjectGroupedBehaviorEnabled) {
            this.loadProjectEvaluationDefinition();
            return;
        }

        this.loadEvaluationStandards();
    }

    public loadProjectEvaluationDefinition(): void {
        const evaluationId = this.selectedEvaluation?.id;

        this.isResolvingLayout = true;

        if (!evaluationId) {
            this.standards = [];
            this.createItem();
            this.isResolvingLayout = false;
            return;
        }

        this.evaluateService.getEvaluation(evaluationId).subscribe({
            next: evaluation => {
                this.resolvedMatrixType = this.resolveMatrixType(evaluation);
                this.groupedMatrices = this.normalizeMatrices(
                    evaluation?.matrices ?? []
                );
                this.standaloneStandards = evaluation?.standards ?? [];

                this.evaluateService
                    .getEvaluationStandards(this.type, evaluationId)
                    .subscribe({
                        next: flatStandards => {
                            this.setFlatStandards(flatStandards);

                            if (this.groupedMatrices.length) {
                                this.standards = this.getFlattenedStandards(
                                    this.groupedMatrices,
                                    this.standaloneStandards
                                );
                                this.createItem();
                                this.isResolvingLayout = false;
                                return;
                            }

                            this.applyStandardsPayload(
                                flatStandards as unknown
                            );
                            this.createItem();
                            this.isResolvingLayout = false;
                        },
                        error: () => {
                            this.isResolvingLayout = false;
                            this.loadEvaluationStandards();
                        },
                    });
            },
            error: () => {
                this.isResolvingLayout = false;
                this.loadEvaluationStandards();
            },
        });
    }

    public loadEvaluationStandards(): void {
        if (!this.selectedEvaluation) {
            this.standards = [];
            this.createItem();
            return;
        }

        this.evaluateService
            .getEvaluationStandards(this.type, this.selectedEvaluation.id)
            .subscribe(items => {
                this.setFlatStandards(items);
                this.applyStandardsPayload(items as unknown);
                this.createItem();
            });
    }

    public processUpdate(): void {
        const records = this.getRecordsForValidation();
        const note = this.item?.note ?? '';

        if (
            this.isSubmitting ||
            !records.length ||
            this.hasEmptyValue ||
            this.hasValueOutOfRange
        ) {
            return;
        }

        this.isSubmitting = true;

        let observable;

        if (this.itemId) {
            observable = this.evaluateService.updateInstance(
                this.itemId,
                this.type,
                records.map((r): EvaluationStandardRecordValue =>
                    this.mapRecordPayload(r, false)
                ),
                note
            );
        } else {
            const entityId = this.entityId;
            const evaluationId = this.selectedEvaluation?.id;

            if (!entityId || !evaluationId) {
                this.isSubmitting = false;
                return;
            }

            const resolvedRecords = this.resolveRecordsForCreate(records);

            observable = this.evaluateService.createInstance(
                entityId,
                this.type,
                evaluationId,
                resolvedRecords.map((r): EvaluationStandardRecordValue =>
                    this.mapRecordPayload(r, true)
                ),
                note
            );
        }

        observable
            .pipe(finalize(() => (this.isSubmitting = false)))
            .subscribe((item: EvaluationInstance) => {
                const successMessage = this.itemId
                    ? 'translate_item_updated_successfully'
                    : 'translate_item_added_successfully';

                this.notificationService.notifySuccess(
                    this.translateService.instant(successMessage)
                );

                this.item = item;
                this.update.emit(item);
            });
    }

    public checkForEmptyValues(): void {
        const records = this.getRecordsForValidation();

        this.hasEmptyValue = records.some(
            x => x.value === undefined || x.value === null
        );

        this.hasValueOutOfRange = records.some(x => this.isValueOutOfRange(x));
    }

    public shouldShowGroupedProjectStandards(): boolean {
        if (!this.isProjectGroupedBehaviorEnabled || this.isResolvingLayout) {
            return false;
        }

        if (this.resolvedMatrixType) {
            return (
                this.resolvedMatrixType === 'multi' &&
                !!this.groupedMatrices?.length
            );
        }

        return !!this.groupedMatrices?.length;
    }

    public getStandardId(standard: EvaluationStandard): string | undefined {
        return standard?.id;
    }

    public getRecordForStandard(
        standard: EvaluationStandard
    ): EvaluationStandardRecord | null {
        const standardId = this.getStandardId(standard);
        const recordById = standardId
            ? (this.recordsByStandardId.get(standardId) ?? null)
            : null;

        if (recordById) return recordById;

        const byKey = (this.item?.records ?? []).find(
            x => this.getEntityKey(x) === this.getEntityKey(standard)
        );

        if (byKey) return byKey;

        const ntKey = this.getNameTargetKey(standard);
        const nameTargetMatches = (this.item?.records ?? []).filter(
            x => this.getNameTargetKey(x) === ntKey
        );

        if (nameTargetMatches.length === 1) {
            return nameTargetMatches[0];
        }

        const targetMatches = (this.item?.records ?? []).filter(
            x => (x.target ?? 0) === (standard.target ?? 0)
        );

        if (targetMatches.length === 1) {
            return targetMatches[0];
        }

        return null;
    }

    public getRecordMinValue(record: EvaluationStandardRecord): number | null {
        return record?.from ?? null;
    }

    public getRecordMaxValue(record: EvaluationStandardRecord): number | null {
        return record?.target ?? null;
    }

    public isValueOutOfRange(record: EvaluationStandardRecord): boolean {
        if (record?.value === undefined || record?.value === null) return false;

        const min = this.getRecordMinValue(record);
        const max = this.getRecordMaxValue(record);

        if (min !== null && record.value < min) return true;
        if (max !== null && record.value > max) return true;

        return false;
    }

    public getLegacyValueOptions(record: EvaluationStandardRecord): number[] {
        const target = record?.target ?? 0;

        if (!this.legacyOptionsCache.has(target)) {
            this.legacyOptionsCache.set(target, [
                0,
                target * 0.25,
                target * 0.5,
                target * 0.75,
                target,
            ]);
        }

        return this.legacyOptionsCache.get(target)!;
    }

    public shouldUseGroupedProjectStandards(): boolean {
        return this.isProjectGroupedBehaviorEnabled;
    }

    public shouldHideNotes(): boolean {
        return this.isNotesHidden;
    }

    public onLegacyValueChange(value: number, idx: number): void {
        if (this.item?.records?.[idx]) {
            this.item.records[idx].value = value;
            this.checkForEmptyValues();
        }
    }

    public getLegacyValueOptionItems(
        record: EvaluationStandardRecord
    ): Array<{ value: number; label: string }> {
        const target = record?.target ?? 0;

        return this.getLegacyValueOptions(record).map(value => ({
            value,
            label: `${target === 0 ? 0 : (value / target) * 100}% (${value})`,
        }));
    }

    public async showGuideDetails(guide?: string): Promise<void> {
        const text = (guide ?? '').trim();
        if (!text) return;

        await this.confirmService.alert({
            title: this.translateService.instant('translate_guide'),
            text,
            confirmButtonText: this.translateService.instant('translate_ok'),
            size: { width: '45%' },
        });
    }

    public getGuideForRecord(record: EvaluationStandardRecord): string {
        const standards = this.standards ?? [];
        const key = this.getEntityKey(record);

        const match =
            standards.find(s => this.resolveId(s) === this.resolveId(record)) ??
            standards.find(s => this.getEntityKey(s) === key);

        return match?.description ?? '';
    }

    private getItem(): void {
        if (!this.itemId) return;

        this.evaluateService
            .getInstance(this.itemId, this.type)
            .subscribe(item => {
                this.item = item;
                this.refreshRecordsMap();

                if (!this.isProjectGroupedBehaviorEnabled) {
                    this.checkForEmptyValues();
                    return;
                }

                this.loadProjectDefinitionForExistingInstance(item);
            });
    }

    private loadProjectDefinitionForExistingInstance(
        item: EvaluationInstance
    ): void {
        this.isResolvingLayout = true;

        const instance = item as EvaluationInstance & { evaluationId?: string };
        const directEvaluationId = instance.evaluationId;

        if (directEvaluationId) {
            this.loadExistingInstanceDefinitionById(directEvaluationId);
            return;
        }

        this.evaluateService
            .listEvaluations(this.type, this.savedEntityId ?? this.entityId)
            .subscribe(items => {
                this.evaluations = items;

                const selectedEvaluation =
                    items.find(x => x.name === item.name) ?? items[0] ?? null;

                this.selectedEvaluation = selectedEvaluation;
                this.resolvedMatrixType =
                    selectedEvaluation?.matrixType ?? null;

                if (!selectedEvaluation?.id) {
                    this.isResolvingLayout = false;
                    this.checkForEmptyValues();
                    return;
                }

                this.loadExistingInstanceDefinitionById(selectedEvaluation.id);
            });
    }

    private loadExistingInstanceDefinitionById(evaluationId: string): void {
        this.evaluateService.getEvaluation(evaluationId).subscribe({
            next: evaluation => {
                this.resolvedMatrixType = this.resolveMatrixType(evaluation);
                this.groupedMatrices = this.normalizeMatrices(
                    evaluation?.matrices ?? []
                );
                this.standaloneStandards = evaluation?.standards ?? [];

                this.evaluateService
                    .getEvaluationStandards(this.type, evaluationId)
                    .subscribe({
                        next: flatStandards => {
                            this.setFlatStandards(flatStandards);

                            this.standards = this.getFlattenedStandards(
                                this.groupedMatrices,
                                this.standaloneStandards
                            );

                            if (!this.standards.length) {
                                this.standards = flatStandards;
                            }

                            this.hydrateItemRecordsWithStandards();
                            this.isResolvingLayout = false;
                        },
                        error: () => {
                            this.standards = [];
                            this.hydrateItemRecordsWithStandards();
                            this.isResolvingLayout = false;
                        },
                    });
            },
            error: () => {
                this.isResolvingLayout = false;
                this.checkForEmptyValues();
            },
        });
    }

    private hydrateItemRecordsWithStandards(): void {
        this.item = {
            ...(this.item ?? {}),
            records: this.buildRecordsFromStandards(this.item?.records ?? []),
        };

        this.refreshRecordsMap();
        this.checkForEmptyValues();
    }

    private applyStandardsPayload(payload: unknown): void {
        if (Array.isArray(payload)) {
            this.standards = payload;
            this.standaloneStandards = payload;
            return;
        }

        const data = payload as {
            standards?: EvaluationStandard[];
            matrices?: EvaluationMatrix[];
        };

        this.groupedMatrices = this.normalizeMatrices(data?.matrices ?? []);
        this.standaloneStandards = data?.standards ?? [];
        this.standards = this.getFlattenedStandards(
            this.groupedMatrices,
            this.standaloneStandards
        );
    }

    private refreshRecordsMap(): void {
        this.recordsByStandardId.clear();

        (this.item?.records ?? []).forEach(record => {
            const standardId = this.resolveRecordStandardId(record);
            if (standardId) {
                this.recordsByStandardId.set(standardId, record);
            }
        });
    }

    private resolveRecordStandardId(
        record: EvaluationStandardRecord
    ): string | undefined {
        return (
            (record as typeof this.recordWithStandardId).standardId ??
            this.standardIdByKey.get(this.getEntityKey(record)) ??
            this.findStandardIdByRange(record.from ?? 0, record.target ?? 0)
        );
    }

    private createItem(): void {
        this.item = {
            note: '',
            records: this.buildRecordsFromStandards(),
        };

        this.refreshRecordsMap();
        this.checkForEmptyValues();
    }

    private resetEvaluationState(selectedEvaluation: Evaluation): void {
        this.selectedEvaluation = selectedEvaluation ?? null;
        this.hasNoEvaluationDefinition = false;
        this.isResolvingLayout = this.isProjectGroupedBehaviorEnabled;
        this.resolvedMatrixType = this.selectedEvaluation?.matrixType ?? null;
        this.groupedMatrices = [];
        this.standaloneStandards = [];
        this.standards = undefined;
        this.standardIdByKey.clear();
    }

    private resolveBehaviorFlags(): void {
        const isProjectEvaluationType = this.type === 'project';
        this.isProjectGroupedBehaviorEnabled =
            isProjectEvaluationType && this.useGroupedProjectStandards;
        this.isNotesHidden = isProjectEvaluationType && this.hideNotes;
    }

    private resolveMatrixType(
        evaluation?: Partial<Evaluation> | null
    ): EvaluationMatrixType | null {
        const matrixType = evaluation?.matrixType;

        if (matrixType === 'multi' || matrixType === 'single') {
            return matrixType;
        }

        const hasAnyMatrix = (evaluation?.matrices ?? []).length > 0;

        return hasAnyMatrix ? 'multi' : 'single';
    }

    private resolveId(entity: {
        id?: string;
        standardId?: string;
        name?: string;
        from?: number | null;
        target?: number | null;
    }): string | undefined {
        return (
            entity.standardId ??
            entity.id ??
            this.standardIdByKey.get(this.getEntityKey(entity)) ??
            this.findStandardIdByRange(entity.from ?? 0, entity.target ?? 0)
        );
    }

    private mapRecordPayload(
        record: EvaluationStandardRecord,
        forCreate: boolean
    ): EvaluationStandardRecordValue {
        const standardId = this.resolveId(record);

        if (forCreate) {
            return {
                standardId,
                value: record.value,
                note: record.note ?? '',
            };
        }

        if (record.id) {
            return {
                id: record.id,
                value: record.value,
                note: record.note ?? '',
            };
        }

        return {
            standardId,
            value: record.value,
            note: record.note ?? '',
        };
    }

    private resolveExistingRecord(
        standardId: string | undefined,
        key: string,
        byId: Map<string, EvaluationStandardRecord>,
        byKey: Map<string, EvaluationStandardRecord>,
        byNameAndTarget: Map<string, EvaluationStandardRecord[]>,
        byTarget: Map<number, EvaluationStandardRecord[]>,
        standard: EvaluationStandard
    ): EvaluationStandardRecord | undefined {
        const byIdOrKey =
            (standardId ? byId.get(standardId) : undefined) ?? byKey.get(key);

        if (byIdOrKey) return byIdOrKey;

        const nameTargetMatches =
            byNameAndTarget.get(this.getNameTargetKey(standard)) ?? [];

        if (nameTargetMatches.length === 1) {
            return nameTargetMatches[0];
        }

        const targetMatches = byTarget.get(standard.target ?? 0) ?? [];

        return targetMatches.length === 1 ? targetMatches[0] : undefined;
    }

    private resolveRecordsForCreate(
        records: EvaluationStandardRecord[]
    ): (typeof this.recordWithStandardId)[] {
        const usedIds = new Set<string>();

        const pickAvailableId = (candidates: string[] = []): string | null => {
            const selectedId = candidates.find(x => !usedIds.has(x));
            if (!selectedId) return null;
            usedIds.add(selectedId);
            return selectedId;
        };

        return records.map((record): typeof this.recordWithStandardId => {
            const strictResolved = this.resolveRecordStandardId(record);
            if (strictResolved && !usedIds.has(strictResolved)) {
                usedIds.add(strictResolved);
                return record;
            }

            const fallbackResolved =
                this.standardIdByKey.get(this.getEntityKey(record)) ??
                this.findStandardIdByRange(
                    record.from ?? 0,
                    record.target ?? 0
                );
            if (fallbackResolved && !usedIds.has(fallbackResolved)) {
                usedIds.add(fallbackResolved);
                return {
                    ...record,
                    standardId: fallbackResolved,
                };
            }

            const ntKey = `${record.name ?? ''}__${record.target ?? 0}`;
            const byNameAndTarget = pickAvailableId(
                this.availableByNameAndTarget.get(ntKey)
            );
            if (byNameAndTarget) {
                return {
                    ...record,
                    standardId: byNameAndTarget,
                };
            }

            const byTarget = pickAvailableId(
                this.availableByTarget.get(record.target ?? 0)
            );
            if (byTarget) {
                return {
                    ...record,
                    standardId: byTarget,
                };
            }

            return record;
        });
    }

    private registerStandardIds(standards: EvaluationStandard[]): void {
        standards.forEach(standard => {
            const standardId = this.getStandardId(standard);
            if (!standardId) return;

            this.standardIdByKey.set(this.getEntityKey(standard), standardId);
        });
    }

    private findStandardIdByRange(
        from: number,
        target: number
    ): string | undefined {
        const matches = this.flatStandardsForSubmit.filter(
            x => (x.from ?? 0) === from && (x.target ?? 0) === target
        );

        if (matches.length !== 1) return undefined;

        return this.getStandardId(matches[0]);
    }

    private getEntityKey(entity: {
        name?: string;
        from?: number | null;
        target?: number | null;
    }): string {
        return `${entity?.name ?? ''}__${entity?.from ?? 0}__${
            entity?.target ?? 0
        }`;
    }

    private getNameTargetKey(entity: {
        name?: string;
        target?: number | null;
    }): string {
        return `${entity?.name ?? ''}__${entity?.target ?? 0}`;
    }

    private getRecordsForValidation(): EvaluationStandardRecord[] {
        const allRecords = this.item?.records ?? [];

        if (!this.shouldShowGroupedProjectStandards()) {
            return allRecords;
        }

        const visibleRecords = new Set<EvaluationStandardRecord>();

        this.getFlattenedStandards(
            this.groupedMatrices,
            this.standaloneStandards
        ).forEach(standard => {
            const record = this.getRecordForStandard(standard);
            if (record) visibleRecords.add(record);
        });

        return visibleRecords.size ? Array.from(visibleRecords) : allRecords;
    }

    private normalizeMatrices(
        matrices: EvaluationMatrix[]
    ): EvaluationMatrix[] {
        return matrices.map(matrix => {
            const matrixName = (matrix.name ?? '').trim().toLowerCase();
            const groups = matrix.groups ?? [];
            const directStandards = this.excludeGroupedStandards(
                matrix.standards ?? [],
                groups
            );

            const hasOnlyMirroredGroups =
                groups.length > 0 &&
                groups.every(
                    group =>
                        ((group.name ?? '').trim().toLowerCase() ||
                            matrixName) === matrixName
                );

            if (!hasOnlyMirroredGroups) {
                return {
                    ...matrix,
                    groups,
                    standards: directStandards,
                };
            }

            return {
                ...matrix,
                groups: [],
                standards: [
                    ...directStandards,
                    ...groups.flatMap(group => group.standards ?? []),
                ],
            };
        });
    }

    private getFlattenedStandards(
        matrices: EvaluationMatrix[],
        standalone: EvaluationStandard[]
    ): EvaluationStandard[] {
        const matrixStandards = matrices.flatMap(matrix => [
            ...(matrix.standards ?? []),
            ...(matrix.groups ?? []).flatMap(group => group.standards ?? []),
        ]);

        return this.dedupeStandardsById([...matrixStandards, ...standalone]);
    }

    private excludeGroupedStandards(
        standards: EvaluationStandard[],
        groups: EvaluationMatrix['groups']
    ): EvaluationStandard[] {
        const groupedIds = this.collectStandardIds(
            (groups ?? []).flatMap(group => group.standards ?? [])
        );

        return standards.filter(standard => {
            const id = this.getStandardId(standard);
            return !id || !groupedIds.has(id);
        });
    }

    // Only ids are safe to compare: two criteria in different matrices may
    // legitimately share the same name and range.
    private dedupeStandardsById(
        standards: EvaluationStandard[]
    ): EvaluationStandard[] {
        const seen = new Set<string>();

        return standards.filter(standard => {
            const id = this.getStandardId(standard);
            if (!id) return true;
            if (seen.has(id)) return false;

            seen.add(id);
            return true;
        });
    }

    private collectStandardIds(standards: EvaluationStandard[]): Set<string> {
        const ids = new Set<string>();

        standards.forEach(standard => {
            const id = this.getStandardId(standard);
            if (id) ids.add(id);
        });

        return ids;
    }

    private setFlatStandards(standards: EvaluationStandard[]): void {
        this.flatStandardsForSubmit = standards;
        this.availableByNameAndTarget.clear();
        this.availableByTarget.clear();

        standards.forEach(standard => {
            const id = this.getStandardId(standard);
            if (!id) return;

            const ntKey = `${standard.name ?? ''}__${standard.target ?? 0}`;
            if (!this.availableByNameAndTarget.has(ntKey)) {
                this.availableByNameAndTarget.set(ntKey, []);
            }
            this.availableByNameAndTarget.get(ntKey)!.push(id);

            const tKey = standard.target ?? 0;
            if (!this.availableByTarget.has(tKey)) {
                this.availableByTarget.set(tKey, []);
            }
            this.availableByTarget.get(tKey)!.push(id);
        });

        this.registerStandardIds(standards);
    }

    private buildRecordsFromStandards(
        existingRecords: EvaluationStandardRecord[] = []
    ): EvaluationStandardRecord[] {
        const byId = new Map<string, EvaluationStandardRecord>();
        const byKey = new Map<string, EvaluationStandardRecord>();
        const byNameAndTarget = new Map<string, EvaluationStandardRecord[]>();
        const byTarget = new Map<number, EvaluationStandardRecord[]>();

        existingRecords.forEach(record => {
            const standardId = this.resolveId(record);
            if (standardId) byId.set(standardId, record);

            const key = this.getEntityKey(record);
            if (!byKey.has(key)) byKey.set(key, record);

            const ntKey = this.getNameTargetKey(record);
            if (!byNameAndTarget.has(ntKey)) byNameAndTarget.set(ntKey, []);
            byNameAndTarget.get(ntKey)!.push(record);

            const target = record.target ?? 0;
            if (!byTarget.has(target)) byTarget.set(target, []);
            byTarget.get(target)!.push(record);
        });

        return (this.standards ?? []).map(standard => {
            const standardId = this.resolveId(standard);
            const key = this.getEntityKey(standard);
            const source = this.resolveExistingRecord(
                standardId,
                key,
                byId,
                byKey,
                byNameAndTarget,
                byTarget,
                standard
            );

            return {
                id: source?.id,
                standardId: standardId,
                name: standard.name ?? source?.name ?? '',
                from: standard.from ?? source?.from,
                target: standard.target ?? source?.target ?? 0,
                value: source?.value,
                note: source?.note,
            } as typeof this.recordWithStandardId;
        });
    }
}
